import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Lock, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { BrandLogo } from '../components/layout/BrandLogo';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signInWithPassword,
    signInWithEmail,
    signInWithGoogle,
    authError,
    clearError,
    hasRemoteAuth,
  } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const redirectTo = location.state?.from?.pathname || '/library';

  const handlePasswordSignIn = async (event) => {
    event.preventDefault();
    clearError();
    setIsLoading(true);

    const { error } = await signInWithPassword(email.trim(), password);
    setIsLoading(false);

    if (!error) {
      toast.success('Signed in successfully.');
      navigate(redirectTo, { replace: true });
    }
  };

  const handleMagicLink = async () => {
    clearError();
    setIsLoading(true);
    const { error } = await signInWithEmail(email.trim());
    setIsLoading(false);
    if (!error) {
      toast.success('Check your inbox for your secure sign-in link.');
    }
  };

  const handleGoogle = async () => {
    clearError();
    setIsLoading(true);
    await signInWithGoogle();
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#fffdf9_0%,#f8efe6_45%,#fdf8f1_100%)] px-4 py-6 md:px-6 md:py-8">
      <div className="mx-auto max-w-6xl">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-full border border-[#E9DDD1] bg-white/90 px-4 py-2 text-sm font-medium text-[#2D241E]"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="mt-6 grid overflow-hidden rounded-[36px] border border-[#E7DED3] bg-[#FFF9F4] shadow-[0_35px_120px_rgba(48,35,24,0.18)] md:grid-cols-[1.02fr_0.98fr]">
          <div className="hidden border-r border-[#ECE2D7] bg-[radial-gradient(circle_at_top_left,rgba(244,227,197,0.55),transparent_34%),linear-gradient(160deg,#F8F0E8_0%,#FBF7F2_45%,#F5EBE2_100%)] p-10 md:flex md:flex-col">
            <BrandLogo
              iconSize={36}
              titleClassName="text-2xl text-[#221B17]"
              subtitleClassName="text-xs tracking-[0.24em] text-[#8B8379]"
            />
            <h1 className="mt-16 max-w-sm font-serif text-5xl leading-[1.02] tracking-[-0.04em] text-[#221B17]">
              Return to your invitation studio.
            </h1>
            <p className="mt-5 max-w-md text-base leading-7 text-[#6F675F]">
              Sign in to continue refining invitations, save revisions, and unlock export-ready delivery.
            </p>
          </div>

          <div className="px-5 py-8 md:px-12 md:py-12">
            <div className="md:hidden">
              <BrandLogo
                iconSize={36}
                titleClassName="text-2xl text-[#221B17]"
                subtitleClassName="text-xs text-[#8B8379]"
              />
            </div>

            <div className="mt-8 text-center md:mt-0 md:text-left">
              <div className="text-xs uppercase tracking-[0.24em] text-[#A2968A]">Studio access</div>
              <h2 className="mt-3 font-serif text-4xl leading-none tracking-[-0.04em] text-[#221B17]">
                Sign in securely
              </h2>
              <p className="mt-4 text-sm leading-7 text-[#7E766F]">
                Use your password-based account for the live backend session, or a remote provider if configured.
              </p>
            </div>

            {authError ? (
              <div className="mt-6 rounded-[22px] border border-[#F2C7C2] bg-[#FFF4F2] px-4 py-4 text-sm text-[#A94D45]">
                {authError}
              </div>
            ) : null}

            <div className="mt-8 rounded-[28px] border border-[#ECE2D7] bg-white p-4 shadow-[0_20px_60px_rgba(77,59,41,0.07)]">
              {hasRemoteAuth ? (
                <>
                  <button
                    onClick={handleGoogle}
                    disabled={isLoading}
                    className="flex w-full items-center justify-center gap-3 rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] px-4 py-4 text-sm font-medium text-[#221B17] transition hover:bg-white disabled:opacity-50"
                  >
                    Continue with Google
                  </button>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[#EFE6DB]" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white px-4 text-[0.7rem] uppercase tracking-[0.24em] text-[#A79C90]">
                        Or password
                      </span>
                    </div>
                  </div>
                </>
              ) : null}

              <form onSubmit={handlePasswordSignIn}>
                <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-[#8E857A]">
                  Email address
                </label>
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A79C90]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] py-4 pl-12 pr-4 text-sm text-[#221B17] outline-none transition focus:border-[#D5C0A3] focus:bg-white"
                    disabled={isLoading}
                    required
                  />
                </div>

                <label className="mb-2 mt-4 block text-xs uppercase tracking-[0.18em] text-[#8E857A]">
                  Password
                </label>
                <div className="relative">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A79C90]" />
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Enter your password"
                    className="w-full rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] py-4 pl-12 pr-4 text-sm text-[#221B17] outline-none transition focus:border-[#D5C0A3] focus:bg-white"
                    disabled={isLoading}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !email.trim() || !password.trim()}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-[22px] bg-[#1F1A17] px-5 py-4 text-sm font-medium text-white transition hover:bg-[#2B2420] disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </form>

              <button
                onClick={handleMagicLink}
                disabled={isLoading || !email.trim()}
                className="mt-3 w-full rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] px-5 py-4 text-sm font-medium text-[#221B17] transition hover:bg-white disabled:opacity-50"
              >
                Send magic link
              </button>
            </div>

            <div className="mt-6 text-center text-sm text-[#7E746B] md:text-left">
              Need an account?{' '}
              <Link className="underline-offset-4 hover:underline" state={{ from: { pathname: redirectTo } }} to="/signup">
                Create one
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
